import { useMutation } from "@apollo/client";
import { Q } from "@nozbe/watermelondb";
import { useDatabase } from "@nozbe/watermelondb/hooks";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
import {
  ProductLocationInventoryPricing,
  StockCountItemStatus,
} from "@towersystems/roam-common/lib/generated-types";
import { useContext } from "react";
import { SyncState } from "../../../../utilities/data/constants";
import Product from "../../../../utilities/data/model/Product";
import StockCount from "../../../../utilities/data/model/StockCount";
import StockCountItem from "../../../../utilities/data/model/StockCountItem";
import StockCountLine from "../../../../utilities/data/model/StockCountLine";
import {
  COUNT_STOCK_COUNT_ITEM,
  StockIdentifier,
} from "../../../../utilities/stock-count";
import { useStockCountItemSyncContext } from "../../../../utilities/stock-count/hooks";
import { CurrentCountContext } from "../../../../utilities/stock-count/context";

export const useStockCountScreenContextProvider = () => {
  const context = useContext(CurrentCountContext);
  if (!context) {
    throw new Error("No StockCountScreenContext context was provided. ");
  }
  return context;
};

export function useStockCounter() {
  const database = useDatabase();
  const online = useOnline();
  const { addCountToQueue } = useStockCountItemSyncContext();

  const [countStockCountItem] = useMutation<{
    countStockCountItem: StockCountItem;
  }>(COUNT_STOCK_COUNT_ITEM);

  async function undoCount(stockCountItemId: string) {
    const batchActions: any[] = [];

    const stockCountItem = await database.collections
      .get<StockCountItem>("stock_count_items")
      .find(stockCountItemId);

    // adjust stockCountLine count by the inverse of the adjustment amount
    const stockCountLine = await stockCountItem.stockCountLine.fetch();
    const product = await stockCountLine.product.fetch();

    if (
      stockCountItem.syncState === SyncState.UPLOADED &&
      stockCountItem.counted
    ) {
      // adjust remote count
      const stockItem = await countStockCountItem({
        variables: {
          stockCountId: stockCountLine.stockCount.id,
          input: {
            productId: product?.id,
            barcode: product?.stockNumber ?? stockCountLine.barcode,
            counted: stockCountItem.counted ? -stockCountItem.counted : 0,
            name: stockCountLine.name,
          },
        },
      });

      const remoteStockCountItem = stockItem.data?.countStockCountItem;

      if (remoteStockCountItem) {
        const createStockCountItem = database
          .get<StockCountItem>("stock_count_items")
          .prepareCreate((newStockCountItem) => {
            newStockCountItem._raw.id = remoteStockCountItem.id;
            newStockCountItem.stockCountLine.id = stockCountLine._raw.id;
            newStockCountItem.status = remoteStockCountItem.status;
            newStockCountItem.counted = remoteStockCountItem.counted;
            newStockCountItem.countedAt = remoteStockCountItem.countedAt;
          });

        batchActions.push(createStockCountItem);
      }
    } else if (stockCountItem.syncState !== SyncState.UPLOADED) {
      // delete local stock count item
      const deleteStockCountItem = stockCountItem.prepareDestroyPermanently();

      batchActions.push(deleteStockCountItem);
    }

    await database.write(async () => {
      await database.batch(batchActions);
    });
  }

  const countStockCountItemByStockIdentifier = async ({
    stockIdentifier,
    stockCountId,
    adjustBy = 1,
  }: {
    stockIdentifier: StockIdentifier;
    stockCountId: string;
    adjustBy: number;
  }) => {
    // create new stock count line if none exists
    const stockCountLine = await findStockCountLineByStockIdentifierOrCreate(
      stockCountId,
      stockIdentifier
    );

    const stockCountItemCollection =
      database.collections.get<StockCountItem>("stock_count_items");

    // // this stock count item must be updated to use the external stock count line id post-upload
    const createdStockCountItem = await database.write(async (writer) => {
      return await stockCountItemCollection.create((stockCountItem) => {
        const now = new Date();
        stockCountItem.status = StockCountItemStatus.PENDING;
        stockCountItem.counted = adjustBy;
        stockCountItem.stockCountLine.id = stockCountLine.id;
        stockCountItem.syncState = SyncState.UPLOAD;
        stockCountItem.createdAt = now;
        stockCountItem.updatedAt = now;
        // force the ui to show that it didnt sync
        if (online === false) {
          stockCountItem.numSyncAttempts = 1;
        }
      });
    });
    await addCountToQueue(createdStockCountItem.id);
  };

  async function countStockItem(
    stockCountId: string,
    stockIdentifier: StockIdentifier,
    adjustBy: number = 1,
    additionalData?: {
      name?: string | null;
      expected?: number | null;
    }
  ) {
    let stockCountLine: StockCountLine | undefined;

    try {
      stockCountLine = await findStockCountLineByStockIdentifierOrCreate(
        stockCountId,
        stockIdentifier
      );

      await stockCountLine?.triggerUpdate();
    } catch (err) {
      console.log(err);
    }

    if (!stockCountLine) {
      return countStockCountItemByStockIdentifier({
        stockIdentifier,
        stockCountId,
        adjustBy,
      });
    }

    if (!stockCountLine.name && additionalData?.name) {
      await stockCountLine.setName(additionalData.name);
    }

    if (!stockCountLine.expected && additionalData?.expected) {
      await stockCountLine.setExpected(additionalData.expected);
    }

    const stockCountItemCollection =
      database.collections.get<StockCountItem>("stock_count_items");

    const createdStockCountItem = await database.write(async () => {
      return await stockCountItemCollection.create((newStockCountItem) => {
        const now = new Date();

        newStockCountItem.status = StockCountItemStatus.PENDING;
        newStockCountItem.counted = adjustBy;
        newStockCountItem.stockCountLine.id = stockCountLine?.id;
        newStockCountItem.syncState = SyncState.UPLOAD;

        newStockCountItem.createdAt = now;
        newStockCountItem.updatedAt = now;
        newStockCountItem.countedAt = now;
        newStockCountItem.numSyncAttempts = online ? 0 : 1;
        newStockCountItem.lastSyncAttempt = null;
      });
    });

    await addCountToQueue(createdStockCountItem.id);

    return {
      stockCountLine,
      stockCountItem: createdStockCountItem,
    };
  }

  async function findProductBySkuOrFail(sku: string) {
    const productCollection = database.collections.get<Product>("products");

    const product = await fetchOne(
      productCollection.query(Q.where("stock_number", sku), Q.take(1)).fetch()
    );
    if (!product) throw Error("Product not found by sku: " + sku);
    return product;
  }

  async function findProductOrFail(productId: string) {
    const productCollection = database.collections.get<Product>("products");

    const product = await fetchOne(
      productCollection.query(Q.where("id", productId), Q.take(1)).fetch()
    );
    if (!product) throw Error("Product not found: " + productId);
    return product;
  }

  async function findStockCountLineByStockIdentifierOrCreate(
    stockCountId: string,
    stockIdentifier: StockIdentifier
  ) {
    const stockCount = await database.collections
      .get<StockCount>("stock_counts")
      .find(stockCountId);

    const stockCountLineCollection =
      database.collections.get<StockCountLine>("stock_count_lines");

    let dynamicAndQuery;

    if (stockIdentifier.productId) {
      dynamicAndQuery = Q.where("product_id", stockIdentifier.productId);
    }

    if (stockIdentifier.barcode) {
      dynamicAndQuery = Q.where("barcode", stockIdentifier.barcode);
    }

    if (!dynamicAndQuery) {
      throw Error("Stock identifier must have a product id or barcode");
    }

    const stockCountLine = await fetchOne(
      stockCountLineCollection
        .query(Q.where("stock_count_id", stockCountId), Q.and(dynamicAndQuery))
        .fetch()
    );

    if (!stockCountLine) {
      const product = stockIdentifier.productId
        ? await findProductOrFail(stockIdentifier.productId).catch(console.log)
        : undefined;

      const expected = product
        ? findPricingForProduct(product, stockCount.location.id)?.stockOnHand ??
          0
        : 0;

      return await database.write(async () => {
        const now = new Date();
        return await stockCountLineCollection.create((item) => {
          item.stockCount.id = stockCountId;
          item.product.id = product ? product.id : null;
          item.expected = expected;
          item.initialExpected = expected;
          item.barcode = product?.stockNumber || stockIdentifier.barcode || "";
          item.createdAt = now;
          item.updatedAt = now;
        });
      });
    }

    return stockCountLine;
  }

  function findPricingForProduct(
    product: Product,
    locationId: string
  ): ProductLocationInventoryPricing | undefined {
    return (
      product.locationInventoryPricing.find(
        (pricing) => pricing.locationId === locationId
      ) || product.locationInventoryPricing[0]
    );
  }

  return {
    countStockItem,
    undoCount,
    findStockCountLineByStockIdentifierOrCreate,
    findProductOrFail,
    findProductBySkuOrFail,
  };
}

async function fetchOne<T>(fetch: Promise<T[]>) {
  return fetch.then((result) => result[0]);
}
